import React, { FC, useEffect, useMemo } from 'react';
import { WindowWidthContext } from '@minecraft.utils';
import {
  FlexCol,
  FlexRow,
  SimpleTable,
  SimpleTableProps,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  LineItem,
  BodyText,
  ClickableIcon,
  ListCardItem,
  ListCardsGroup,
  ListCardItemProps,
  TableRowProps,
} from '@minecraft.atoms';

export type DefaultTableItem = { id: string | number };

export type ExpandedCellState = { [rowId: string | number]: boolean };

export type UseExpandCellHookProps = {
  rows: Array<{ id: string | number }>;
  callback?: (expandedCellState: ExpandedCellState) => void;
};

export interface SimpleTableCellRendererProps<T extends DefaultTableItem> {
  /** the current row's data (the full object) */
  value: T;
  /** index of the row being rendered */
  rowIndex: number;
  /** name of the `field` value from the column defi */
  columnField: keyof T;
  /** whether the window size has converted the table to mobile view */
  isMobile: boolean;
  /** whether the row is expanded */
  expanded: boolean;
}

export type ColumnDef<T extends DefaultTableItem, RP extends Record<string, any> = Record<string, any>> = {
  // a unique identifier for the column, useful if your "field" is not unique because it resolves to an object
  id?: string;
  // a key in the object that is in the data array
  field: keyof T;
  // label value to use in the column header
  label?: string;
  // a11y label to use for the column
  'aria-label'?: string;
  // whether the column expands when toggled
  expandable?: boolean;
  // a function to format the value returned by `object[field]`
  valueFormatter?: (value: T[keyof T]) => string;
  // a component to render the cell content instead of using the plain value
  // it gets passed the row data, any values passed in `cellRendererProps`, an `isMobile` flag,
  // the name of the `field` and the index of the row being rendered
  CellRenderer?: FC<SimpleTableCellRendererProps<T> & RP>;
  // a set of props to pass to the `CellRenderer` component
  cellRendererProps?: RP;
  // set the relative size of this column compared to the others
  flex?: number;
};

export type ExpandableSimpleTableTranslations = {
  collapsedButton: (rowIdentifier: string) => string;
  expandedButton: (rowIdentifier: string) => string;
  tableCaption: string;
  actionsColumnLabel: string;
};

export type ColumnDefBuilderParams = {
  isMobile: boolean;
};

export type ExpandableSimpleTableProps<T extends DefaultTableItem> = {
  /** an array of column definition object, or a function that returns an array of column definition objects */
  columnDefs: Array<ColumnDef<T>> | ((params: ColumnDefBuilderParams) => Array<ColumnDef<T>>);
  /** an array of data objects, each one will render a row in the table */
  data?: T[];
  /** a map of rowIds that should be expanded */
  expandedCells: { [rowId: string | number]: boolean };
  /** callback invoked when expanded rows change */
  toggleExpanded: (rowId: string | number) => void;
  /**
   * The keyname of the field that uniquely identifies a row and is human-readable
   * This is used for accessibility purposes for the actions within a row
   */
  rowIdentifier: string;
  translations: ExpandableSimpleTableTranslations;
  /** this will hide the expanded row toggle column and button */
  hideExpansion?: boolean;
  /** props to pass to the underlying SimpleTable component in desktop size */
  tableProps?: Omit<SimpleTableProps, 'caption'>;
  /** props to pass to the underlying ListCardItem component in mobile size */
  mobileCardProps?: ListCardItemProps;
  /** props to pass to the underlying TableRow component */
  rowProps?: Omit<TableRowProps, 'data-rowid' | 'data-testid' | 'data-qa-id'>;
  /** props to pass to the underlying TableCell component */
  cellProps?: string;
};

export const useExpandCellHook = ({ rows, callback }: UseExpandCellHookProps) => {
  const [expandedCells, setExpandedCells] = React.useState<ExpandedCellState>({});

  useEffect(() => {
    // Initialize all the rows to be collapsed
    const newExpandedCells: ExpandedCellState = rows?.reduce((acc, row) => {
      acc[row.id] = false;

      return acc;
    }, {});

    setExpandedCells(newExpandedCells ?? {});
  }, []);

  const toggleExpanded = (rowId: string | number) => {
    const direction = !expandedCells[rowId];
    callback?.({ ...expandedCells, [rowId]: direction });
    setExpandedCells({ ...expandedCells, [rowId]: direction });
  };

  return { expandedCells, toggleExpanded };
};

const getColumnWidths = <T extends DefaultTableItem>(columnDefs: Array<Pick<ColumnDef<T>, 'flex'>>) => {
  // First find the total "flex" we have across the table
  let totalFlex = 0;
  columnDefs.forEach((columnDef) => {
    totalFlex += columnDef.flex ?? 0;
  });
  // Divide that total flex amongst the columns that flex set and are not flex 0
  const perFlexPercentage = 100 / totalFlex;

  // Return an array that has these flex percentages per columnDef
  return columnDefs.map((columnDef) => {
    if (columnDef.flex != null) {
      return perFlexPercentage * columnDef.flex;
    }

    return undefined;
  });
};

const getColumnValue = <T extends DefaultTableItem>(
  columnDef: Omit<ColumnDef<T>, 'CellRenderer' | 'cellRendererProps'>,
  value: T[keyof T]
) => {
  return columnDef.valueFormatter ? columnDef.valueFormatter(value) : value;
};

/**
 * A responsive table that allows you to truncate and expand specific sections (rows or cell depending on screen)
 * The `useExpandCellHook` is a default implementation of the expanded rows and is required to use this component
 *
 * [Figma Diagram](https://www.figma.com/file/AiDAD1WpprgfbZI2XV6hU1/Casting-Networks-Design-System?node-id=5773%3A137173&t=wvyq8jiS72WjAYMY-0)
 * See "ListRowCardGroup"
 *
 * @example
 * const { expandedCells, toggleExpanded } = useExpandCellHook({ rows: data });
 * return (
 *  <ExpandableSimpleTable
 *    expandedRows={expandedCells}
 *    rowIdentifier="identifierKey"
 *    toggleExpanded={toggleExpanded}
 *    translations={{
 *      expandedButton: (identifierValueFromKey) => t('your translation', { identifierValueFromKey }),
 *      collapsedButton: (identifierValueFromKey) => t('your translation', { identifierValueFromKey }),
 *      tableCaption: t('translation key to describe the table'),
 *      actionsColumnLabel: t('the translation key for the column that contains actions'),
 *    }}
 *    columnDefs={[
 *      {
 *        field: 'fieldNameA',
 *        label: t('your translation key'),
 *        valueFormatter: (value) => formatIntlRelativeTime({ date: value }),
 *      },
 *      { field: 'fieldNameB', label: t('your translation key') },
 *    ]}
 *    data={data}
 *  />
 * )
 *
 */
export const ExpandableSimpleTable = <T extends DefaultTableItem>({
  data,
  expandedCells,
  toggleExpanded,
  columnDefs: columnDefsProp,
  rowIdentifier,
  translations,
  hideExpansion = false,
  tableProps = {},
  mobileCardProps = {},
  rowProps = {},
}: ExpandableSimpleTableProps<T>) => {
  const { lessMd: isMobile } = WindowWidthContext.useWidthData();
  const columnDefs = useMemo(() => {
    if (typeof columnDefsProp === 'function') {
      return columnDefsProp({ isMobile });
    }

    return columnDefsProp;
  }, [columnDefsProp, isMobile]);
  const columnWidths = useMemo(() => getColumnWidths<T>(columnDefs), [columnDefs]);

  // Mobile "first" displayed as an unordered list so screen readers can describe
  // how many elements there are in the list.

  if (isMobile) {
    return (
      <ListCardsGroup>
        {data?.map((row, rowIndex) => (
          <ListCardItem
            bordered={tableProps?.bordered}
            {...mobileCardProps}
            data-testid={`table-row-${row.id}`}
            data-qa-id={`table-row-${row.id}`}
            key={row.id}
          >
            <FlexCol gap="2">
              {columnDefs?.map(({ CellRenderer, cellRendererProps = {}, ...column }) => {
                return (
                  <LineItem
                    key={column?.id || column?.field?.toString()}
                    data-testid={`column-${column?.id || column?.field?.toString()}`}
                  >
                    <FlexRow justifyContent="space-between">
                      <BodyText fontWeight="bold">{column.label}</BodyText>
                      {column.expandable ? (
                        <ClickableIcon
                          name={expandedCells[row.id] ? 'chevron-open-thin' : 'chevron-closed-thin'}
                          aria-label={
                            expandedCells[row.id]
                              ? translations.collapsedButton(row[rowIdentifier])
                              : translations.expandedButton(row[rowIdentifier])
                          }
                          onClick={() => toggleExpanded(row.id)}
                          full={false}
                          block={false}
                        />
                      ) : null}
                    </FlexRow>
                    {CellRenderer && (
                      <CellRenderer
                        isMobile={isMobile}
                        rowIndex={rowIndex}
                        columnField={column?.field}
                        value={row}
                        expanded={expandedCells[row.id]}
                        {...cellRendererProps}
                      />
                    )}
                    {!CellRenderer && (
                      <BodyText className={expandedCells[row.id] ? '' : 'cn_line-clamp-2'}>
                        {getColumnValue(column, row[column.field])}
                      </BodyText>
                    )}
                  </LineItem>
                );
              })}
            </FlexCol>
          </ListCardItem>
        ))}
      </ListCardsGroup>
    );
  }

  // Desktop we display as a standard table
  return (
    <SimpleTable {...tableProps} caption={translations.tableCaption}>
      <TableHead>
        <TableRow {...rowProps}>
          {columnDefs?.map((column, columnIndex) => (
            <TableHeader
              width={columnWidths[columnIndex] != null ? columnWidths[columnIndex] : null}
              key={column?.id || column.field?.toString()}
              aria-label={!column.label ? column['aria-label']?.toString() : column.label?.toString()}
            >
              {column.label}
            </TableHeader>
          ))}
          {!hideExpansion && <TableHeader aria-label={translations.actionsColumnLabel} />}
        </TableRow>
      </TableHead>
      <TableBody>
        {data?.map((row, rowIndex) => (
          <TableRow
            {...rowProps}
            data-rowid={row.id?.toString()}
            key={row.id}
            data-testid={`table-row-${row.id}`}
            data-qa-id={`table-row-${row.id}`}
          >
            {columnDefs?.map(({ CellRenderer, cellRendererProps = {}, ...column }) => {
              return (
                <TableCell
                  key={column?.id || column.field?.toString()}
                  className={expandedCells[row.id] ? '' : 'cn_truncate'}
                  maxWidth={column.expandable && !expandedCells[row.id] ? 0 : null}
                >
                  {CellRenderer && (
                    <CellRenderer
                      isMobile={isMobile}
                      rowIndex={rowIndex}
                      columnField={column?.field}
                      value={row}
                      expanded={expandedCells[row.id]}
                      {...cellRendererProps}
                    />
                  )}
                  {!CellRenderer && getColumnValue(column, row[column.field])}
                </TableCell>
              );
            })}
            {!hideExpansion && (
              <TableCell>
                <ClickableIcon
                  className="cn_mr-2"
                  name={expandedCells[row.id] ? 'chevron-open-thin' : 'chevron-closed-thin'}
                  aria-label={
                    expandedCells[row.id]
                      ? translations.collapsedButton(row[rowIdentifier])
                      : translations.expandedButton(row[rowIdentifier])
                  }
                  onClick={() => toggleExpanded(row.id)}
                />
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </SimpleTable>
  );
};
